import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import './ManageProjectUsersModal.css';
import { baseApi } from '../../../../API/api';
import { backdropCloseHandlers } from '../../../../utils/backdropClose';

/** Axios config carrying the current Cognito idToken; the backend verifies it. */
async function authConfig() {
  const idToken = (await Auth.currentSession()).getIdToken().getJwtToken();
  return { headers: { Authorization: `Bearer ${idToken}` } };
}

// Material Symbols font is loaded globally in index.html with all needed icons (accessibility_new, delete, explore)
// No need to dynamically load it here

const ROLE_OPTIONS = [
  { value: 1, label: 'Administrator' },
  { value: 2, label: 'Editor' },
  { value: 3, label: 'Viewer' }
];

// Default new members to the least-privileged role (Viewer), not Administrator,
// so an unchanged dropdown can't silently grant admin access.
const defaultRoleValue = ROLE_OPTIONS.find((opt) => opt.label === 'Viewer').value;

function getUserInitials(email = '') {
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase() || '?';
}

const ManageProjectUsersModal = ({ projectId, projectName = '', isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState(defaultRoleValue);
  const [formMessage, setFormMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [feedbackExiting, setFeedbackExiting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);

  const modalTitle = useMemo(() => projectName || 'Manage project team', [projectName]);

  useEffect(() => {
    if (isOpen) {
      fetchProjectUsers();
    } else {
      setFormEmail('');
      setFormMessage('');
      setFormRole(defaultRoleValue);
      setFormSuccess('');
      setFormError('');
      setFeedbackExiting(false);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId]);

  useEffect(() => {
    if (!formError && !formSuccess) {
      setFeedbackExiting(false);
      return;
    }
    const exitStart = setTimeout(() => setFeedbackExiting(true), 4700);
    const clear = setTimeout(() => {
      setFormError('');
      setFormSuccess('');
      setFeedbackExiting(false);
    }, 5000);
    return () => {
      clearTimeout(exitStart);
      clearTimeout(clear);
    };
  }, [formError, formSuccess]);

  const fetchProjectUsers = async () => {
    if (!projectId) return;
    setLoading(true);
    setFormError('');
    try {
      const response = await axios.get(`${baseApi}/api/projects/${projectId}/users`, await authConfig());
      setUsers(response.data?.users || []);
    } catch (error) {
      console.error('Failed to load project users:', error);
      setFormError(error.response?.data?.error || 'Failed to load project users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const trimmed = email.trim();
    if (!trimmed) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Please enter a valid email address';
    return '';
  };

  const handleAddUser = async (event) => {
    event.preventDefault();
    setFormError('');
    setFormSuccess('');

    const validationError = validateEmail(formEmail);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      // The backend verifies the requester's identity (and role) from the
      // signed idToken in this config, not from any spoofable body field.
      const response = await axios.post(
        `${baseApi}/api/projects/${projectId}/users`,
        {
          email: formEmail.trim(),
          role_id: formRole,
          message: formMessage
        },
        await authConfig()
      );
      setFormEmail('');
      setFormMessage('');
      // The user is on the project regardless, but the Cognito invite step is
      // best-effort and gated by the requester's role.
      const inviteStatus = response.data?.invite_status;
      if (inviteStatus === 'forbidden_not_admin') {
        setFormError('User added to the project but not to 123bimlive. Please contact an Administrator user.');
      } else if (inviteStatus === 'failed') {
        setFormError('User added to the project, but creating their login invite failed. Please contact an admin.');
      } else {
        setFormSuccess('User added to the project successfully.');
      }
      await fetchProjectUsers();
    } catch (error) {
      console.error('Failed to add user to project:', error);
      const backendResponse = error.response?.data;
      if (backendResponse?.code === 'USER_ALREADY_ASSIGNED') {
        // Already a project member, but Cognito was still reconciled.
        if (backendResponse.invite_status === 'forbidden_not_admin') {
          setFormError('User is already on the project but not in 123bimlive. Please contact an Administrator user.');
        } else if (backendResponse.invite_status === 'failed') {
          setFormError('User is already on the project, but creating their login invite failed. Please contact an admin.');
        } else if (backendResponse.invite_status === 'sent') {
          setFormEmail('');
          setFormSuccess('User is already on the project. A login invite email was sent.');
        } else {
          setFormError('This user is already part of the project.');
        }
      } else if (backendResponse?.code === 'USER_NOT_FOUND') {
        setFormError('We could not find a user with that email address.');
      } else {
        setFormError(backendResponse?.error || error.message || 'Unable to add user right now.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    setFormError('');
    setFormSuccess('');
    setRemovingUserId(userId);
    try {
      await axios.delete(`${baseApi}/api/projects/${projectId}/users/${userId}`, await authConfig());
      setFormSuccess('User removed from the project.');
      await fetchProjectUsers();
    } catch (error) {
      console.error('Failed to remove user from project:', error);
      setFormError(error.response?.data?.error || error.message || 'Unable to remove user right now.');
    } finally {
      setRemovingUserId(null);
    }
  };

  if (!isOpen) return null;

  const modalMarkup = (
    <div
      className="mpu-overlay"
      {...backdropCloseHandlers(() => onClose?.())}
      role="dialog"
      aria-modal="true"
    >
      <div className="mpu-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <header className="mpu-header">
          <div className="mpu-header-left">
            <div className="mpu-header-icon-wrap">
              <i className="fa-solid fa-users" aria-hidden="true" />
            </div>
            <div>
              <h2 className="mpu-title">{modalTitle}</h2>
              <p className="mpu-subtitle">Add or remove team members who can access this project.</p>
            </div>
          </div>
          <button
            type="button"
            className="mpu-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        {/* Body */}
        <div className="mpu-body">

          {/* Left: form */}
          <section className="mpu-form-section">
            <p className="mpu-section-label">Invite a member</p>
            <form className="mpu-form" onSubmit={handleAddUser}>

              <div className="mpu-row">
                <div className="mpu-field mpu-field-email">
                  <label htmlFor="mpu-email">Email address</label>
                  <input
                    id="mpu-email"
                    type="email"
                    placeholder="user@example.com"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    disabled={submitting}
                    autoComplete="off"
                  />
                </div>
                <div className="mpu-field mpu-field-role">
                  <label htmlFor="mpu-role">Role</label>
                  <select
                    id="mpu-role"
                    value={formRole}
                    onChange={(e) => setFormRole(Number(e.target.value))}
                    disabled={submitting}
                  >
                    {ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mpu-field">
                <label htmlFor="mpu-message">
                  Message <span className="mpu-optional">(optional)</span>
                </label>
                <textarea
                  id="mpu-message"
                  placeholder="Add a personal note to the invitation…"
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="mpu-form-footer">
                <div className={`mpu-feedback ${formError || formSuccess ? 'mpu-feedback-visible' : ''} ${feedbackExiting ? 'mpu-feedback-exiting' : ''}`}>
                  {formError && (
                    <span className="mpu-feedback-error">
                      <i className="fa-solid fa-circle-xmark" aria-hidden="true" />
                      {formError}
                    </span>
                  )}
                  {formSuccess && (
                    <span className="mpu-feedback-success">
                      <i className="fa-solid fa-circle-check" aria-hidden="true" />
                      {formSuccess}
                    </span>
                  )}
                </div>
                <button type="submit" className="mpu-btn-invite" disabled={submitting}>
                  <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                  {submitting ? 'Sending…' : 'Send Invite'}
                </button>
              </div>

            </form>
          </section>

          {/* Right: user list */}
          <section className="mpu-list-section">
            <p className="mpu-section-label">
              Team members
              {!loading && users.length > 0 && (
                <span className="mpu-count">{users.length}</span>
              )}
            </p>
            <div className="mpu-list-wrapper">
              {loading ? (
                <div className="mpu-state-box">
                  <i className="fa-solid fa-circle-notch fa-spin mpu-state-icon" aria-hidden="true" />
                  <span>Loading members…</span>
                </div>
              ) : users.length === 0 ? (
                <div className="mpu-state-box">
                  <i className="fa-solid fa-user-group mpu-state-icon mpu-state-icon-dim" aria-hidden="true" />
                  <span>No members yet. Invite someone above.</span>
                </div>
              ) : (
                <ul className="mpu-list">
                  {users.map((user) => {
                    const rowKey = user.composite_key || `${projectId}_${user.user_id}`;
                    const roleLabel = user.role_label || ROLE_OPTIONS.find((r) => r.value === user.role_id)?.label || `Role ${user.role_id}`;
                    const roleSlug = roleLabel.toLowerCase();
                    return (
                      <li key={rowKey} className="mpu-list-item">
                        <div className={`mpu-avatar mpu-avatar-${roleSlug}`}>
                          {getUserInitials(user.email)}
                        </div>
                        <div className="mpu-user-info">
                          <span className="mpu-user-email">{user.email}</span>
                          <span className={`mpu-role-badge mpu-role-${roleSlug}`}>{roleLabel}</span>
                        </div>
                        <button
                          type="button"
                          className="mpu-remove-btn"
                          onClick={() => handleRemoveUser(user.user_id)}
                          disabled={removingUserId === user.user_id}
                          title="Remove from project"
                          aria-label={`Remove ${user.email}`}
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">delete</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        </div>

      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalMarkup;
  return createPortal(modalMarkup, document.body);
};

export default ManageProjectUsersModal;
